import type { ReactElement } from 'react';
import { useEffect } from 'react';
import { isAlphaInviteOnlyUi, resolveAppUrl, useAuth } from '@fetmode/app-core';
import { t, useLocale } from '@fetmode/i18n';
import { Link, useLocation } from 'react-router-dom';

import { useStudioModel } from '../studio/index.js';
import './studio-recruit.css';
import './studio-recruit-read.css';
import './studio-recruit-hero-ink.css';

const EARN_KEYS = ['Tips', 'Menu', 'Toys', 'Games', 'Jukebox', 'Shows'] as const;
const MODE_KEYS = ['Main', 'Party', 'Ticket', 'Club', 'Vip', 'Exclusive', 'Peep'] as const;
const PERK_KEYS = ['Hours', 'Geo', 'Charge', 'Tax', 'Roxy', 'Mobile', 'Couple', 'Promo'] as const;
const CHIP_KEYS = ['chipFree', 'chipNoFee', 'chipRates', 'chipPromo'] as const;
const NEED_KEYS = ['needAge', 'needCam', 'needTime'] as const;

function RecruitCtas(): ReactElement {
  const { user } = useAuth();
  const { profile } = useStudioModel();
  const location = useLocation();
  const approved = Boolean(profile?.canBroadcast);
  const inviteOnly = isAlphaInviteOnlyUi(import.meta.env);
  const acceptInviteHref = `${resolveAppUrl('consumer', import.meta.env).replace(/\/$/, '')}/accept-invite`;
  const loginState =
    typeof location.state === 'object' &&
    location.state !== null &&
    'from' in location.state
      ? location.state
      : { from: '/onboarding' };

  if (approved) {
    return (
      <div className="studio-recruit__actions">
        <p className="studio-recruit__approved">{t('common.studio.recruit.alreadyApproved')}</p>
        <Link className="studio-recruit__btn studio-recruit__btn--primary" to="/live">
          {t('common.studio.recruit.openBroadcast')}
        </Link>
      </div>
    );
  }

  if (user) {
    return (
      <div className="studio-recruit__actions">
        <Link className="studio-recruit__btn studio-recruit__btn--primary" to="/onboarding">
          {profile
            ? t('common.studio.recruit.ctaContinue')
            : t('common.studio.recruit.ctaStart')}
        </Link>
      </div>
    );
  }

  return (
    <div className="studio-recruit__actions">
      {inviteOnly ? (
        <a className="studio-recruit__btn studio-recruit__btn--primary" href={acceptInviteHref}>
          {t('common.nav.haveInvite')}
        </a>
      ) : (
        <Link className="studio-recruit__btn studio-recruit__btn--primary" to="/register">
          {t('common.studio.recruit.ctaRegister')}
        </Link>
      )}
      <Link
        className="studio-recruit__btn studio-recruit__btn--ghost"
        to="/login"
        state={loginState}
      >
        {t('common.studio.recruit.ctaLogin')}
      </Link>
    </div>
  );
}

export function StudioRecruitPage(): ReactElement {
  const { locale } = useLocale();

  useEffect(() => {
    const previous = document.title;
    document.title = t('common.studio.recruit.title');
    return () => {
      document.title = previous;
    };
  }, [locale]);

  return (
    <article className="studio-recruit">
      <section className="studio-recruit__hero">
        <div className="studio-recruit__hero-inner">
          <div>
            <p className="studio-recruit__kicker">{t('common.studio.recruit.kicker')}</p>
            <h1 className="studio-recruit__title">{t('common.studio.recruit.title')}</h1>
            <p className="studio-recruit__lede">{t('common.studio.recruit.lede')}</p>
            <ul className="studio-recruit__chips">
              {CHIP_KEYS.map((key) => (
                <li key={key}>{t(`common.studio.recruit.${key}`)}</li>
              ))}
            </ul>
            <RecruitCtas />
          </div>
          <aside className="studio-recruit__share" aria-label={t('common.studio.recruit.shareKicker')}>
            <p className="studio-recruit__share-kicker">{t('common.studio.recruit.shareKicker')}</p>
            <p className="studio-recruit__share-title">{t('common.studio.recruit.shareTitle')}</p>
            <p className="studio-recruit__share-keep">
              <span>{t('common.studio.recruit.shareKeepLabel')}</span>
              <strong>{t('common.studio.recruit.shareKeepValue')}</strong>
              <em>{t('common.studio.recruit.shareKeepUnit')}</em>
            </p>
            <dl className="studio-recruit__share-rows">
              <div>
                <dt>{t('common.studio.recruit.shareOffline')}</dt>
                <dd>{t('common.studio.recruit.shareOfflineValue')}</dd>
              </div>
              <div>
                <dt>{t('common.studio.recruit.shareDesktop')}</dt>
                <dd>{t('common.studio.recruit.shareDesktopValue')}</dd>
              </div>
              <div>
                <dt>{t('common.studio.recruit.shareWeb')}</dt>
                <dd>{t('common.studio.recruit.shareWebValue')}</dd>
              </div>
            </dl>
            <p className="studio-recruit__share-foot">{t('common.studio.recruit.shareFoot')}</p>
          </aside>
        </div>
      </section>

      <section className="studio-recruit__section studio-recruit__section--band">
        <div className="studio-recruit__wrap">
          <h2>{t('common.studio.recruit.easyTitle')}</h2>
          <p className="studio-recruit__section-lede">{t('common.studio.recruit.easyLede')}</p>
          <ol className="studio-recruit__steps">
            <li>
              <span className="studio-recruit__step-num" aria-hidden>
                1
              </span>
              <h3>{t('common.studio.recruit.step1Title')}</h3>
              <p>{t('common.studio.recruit.step1Body')}</p>
            </li>
            <li>
              <span className="studio-recruit__step-num" aria-hidden>
                2
              </span>
              <h3>{t('common.studio.recruit.step2Title')}</h3>
              <p>{t('common.studio.recruit.step2Body')}</p>
            </li>
            <li>
              <span className="studio-recruit__step-num" aria-hidden>
                3
              </span>
              <h3>{t('common.studio.recruit.step3Title')}</h3>
              <p>{t('common.studio.recruit.step3Body')}</p>
            </li>
          </ol>
        </div>
      </section>

      <section className="studio-recruit__section">
        <div className="studio-recruit__wrap">
          <h2>{t('common.studio.recruit.earnTitle')}</h2>
          <p className="studio-recruit__section-lede">{t('common.studio.recruit.earnLede')}</p>
          <div className="studio-recruit__grid studio-recruit__grid--earn">
            {EARN_KEYS.map((key) => (
              <article key={key} className="studio-recruit__card">
                <h3>{t(`common.studio.recruit.earn${key}Title`)}</h3>
                <p>{t(`common.studio.recruit.earn${key}Body`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="studio-recruit__section studio-recruit__section--band">
        <div className="studio-recruit__wrap">
          <h2>{t('common.studio.recruit.modesTitle')}</h2>
          <p className="studio-recruit__section-lede">{t('common.studio.recruit.modesLede')}</p>
          <ul className="studio-recruit__modes">
            {MODE_KEYS.map((key) => (
              <li key={key}>
                <h3>{t(`common.studio.recruit.mode${key}`)}</h3>
                <p>{t(`common.studio.recruit.mode${key}Body`)}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="studio-recruit__section">
        <div className="studio-recruit__wrap">
          <h2>{t('common.studio.recruit.splitTitle')}</h2>
          <p className="studio-recruit__section-lede">{t('common.studio.recruit.splitLede')}</p>
          <div className="studio-recruit__split">
            <div>
              <div className="studio-recruit__bar" aria-hidden>
                <div className="studio-recruit__bar-you">{t('common.studio.recruit.splitYou')}</div>
                <div className="studio-recruit__bar-platform">{t('common.studio.recruit.splitPlatform')}</div>
              </div>
              <ul className="studio-recruit__split-list">
                <li>
                  <span>{t('common.studio.recruit.splitFan')}</span>
                  <strong>100</strong>
                </li>
                <li>
                  <span>{t('common.studio.recruit.splitYou')}</span>
                  <strong>70–90</strong>
                </li>
                <li>
                  <span>{t('common.studio.recruit.splitPlatform')}</span>
                  <strong>—</strong>
                </li>
              </ul>
            </div>
            <p className="studio-recruit__split-note">{t('common.studio.recruit.splitNote')}</p>
          </div>
        </div>
      </section>

      <section className="studio-recruit__section studio-recruit__section--band">
        <div className="studio-recruit__wrap">
          <h2>{t('common.studio.recruit.perksTitle')}</h2>
          <div className="studio-recruit__grid studio-recruit__grid--perks">
            {PERK_KEYS.map((key) => (
              <article key={key} className="studio-recruit__perk">
                <h3>{t(`common.studio.recruit.perk${key}Title`)}</h3>
                <p>{t(`common.studio.recruit.perk${key}Body`)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="studio-recruit__section">
        <div className="studio-recruit__wrap">
          <h2>{t('common.studio.recruit.needTitle')}</h2>
          <ul className="studio-recruit__need">
            {NEED_KEYS.map((key) => (
              <li key={key}>
                <p>{t(`common.studio.recruit.${key}`)}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="studio-recruit__section studio-recruit__section--final">
        <div className="studio-recruit__wrap">
          <h2>{t('common.studio.recruit.finalTitle')}</h2>
          <p className="studio-recruit__section-lede">{t('common.studio.recruit.finalLede')}</p>
          <RecruitCtas />
          <p className="studio-recruit__age">{t('common.studio.recruit.ageNote')}</p>
        </div>
      </section>
    </article>
  );
}
